import './Premium.css';

import React, { useState } from 'react';

import { UserPaymentPlanOptionByQuoteId } from '../../../../generated/payment-graphql';
import {
  formatCADCentsOptional,
  formatCADCentsOptionalWithTwoDecimals
} from '../../../../utils/currencyFormat';
import Boolean from './PremiumSwitch';

interface PremiumInput {
  isEndorsement: boolean;
  yearlyPremium: number;
  // Payment plan options keyed by lobType (e.g. "GL", "EO"); each value is the
  // array of options for that product in the application group.
  paymentOptionData?: Record<string, Array<UserPaymentPlanOptionByQuoteId>>;
}

// The aggregated monthly figures shown across every product (lobType).
interface AggregatedMonthlyPaymentOption {
  installmentAmount: number;
  downPayment: number;
  numberOfInstallments: number;
}

const getMaxMonthlyPaymentOptionObject = (
  paymentOptionList: UserPaymentPlanOptionByQuoteId[]
) => {
  const maxPaymentOptionObject = paymentOptionList?.reduce(
    (maxPaymentOptionObject, paymentOption) => {
      if (!maxPaymentOptionObject) {
        return paymentOption;
      }
      return paymentOption.numberOfInstallments >
        maxPaymentOptionObject.numberOfInstallments
        ? paymentOption
        : maxPaymentOptionObject;
    }
  );
  // Do not return yearly option
  if (maxPaymentOptionObject?.numberOfInstallments === 0) {
    return undefined;
  }
  return maxPaymentOptionObject;
};

// Aggregates the per-product monthly options into a single set of figures.
// The payment plan to display is decided by the first product's max monthly
// option; every other product then contributes the option sharing that same
// paymentPlanId, so all products are billed on the same plan. The monthly
// premium and amount due now are the sum across products, while the number of
// installments comes from the chosen plan (identical across matched options).
const getAggregatedMonthlyPaymentOption = (
  paymentOptionData: Record<string, UserPaymentPlanOptionByQuoteId[]>
): AggregatedMonthlyPaymentOption | undefined => {
  const [firstProductList, ...otherProductLists] = Object.values(
    paymentOptionData
  );

  const firstProductOption =
    firstProductList && getMaxMonthlyPaymentOptionObject(firstProductList);

  if (!firstProductOption) {
    return undefined;
  }

  const { paymentPlanId } = firstProductOption;

  // For the remaining products, pick the option on the same payment plan.
  const matchingOptions = otherProductLists
    .map((paymentOptionList) =>
      paymentOptionList.find(
        (paymentOption) => paymentOption.paymentPlanId === paymentPlanId
      )
    )
    .filter(
      (paymentOption): paymentOption is UserPaymentPlanOptionByQuoteId =>
        paymentOption !== undefined
    );

  return [
    firstProductOption,
    ...matchingOptions
  ].reduce<AggregatedMonthlyPaymentOption>(
    (aggregated, paymentOption) => ({
      installmentAmount:
        aggregated.installmentAmount + paymentOption.installmentAmount,
      downPayment: aggregated.downPayment + paymentOption.downPayment,
      numberOfInstallments: paymentOption.numberOfInstallments
    }),
    { installmentAmount: 0, downPayment: 0, numberOfInstallments: 0 }
  );
};

const showYearlyPremium = (yearlyPremium: number, isEndorsement: boolean) => (
  <b className="font-arial text-6xl text-quartary sm:text-5xl pt-2">
    {formatCADCentsOptional.format(yearlyPremium)}
    <p className="text-xl sm:text-sm font-medium">
      {isEndorsement ? 'Premium Change' : 'Annual Premium'}
    </p>
  </b>
);

const showMonthlyPremium = (maxObj: AggregatedMonthlyPaymentOption) => {
  const monthlyPremium = maxObj.installmentAmount;
  const dueAmount = maxObj.downPayment;
  const monthInstall = maxObj.numberOfInstallments;
  const monthlyPremiumDisplayValue = formatCADCentsOptionalWithTwoDecimals.format(
    monthlyPremium
  );
  const [
    monthlyPremiumInteger,
    monthlyPremiumDecimals
  ] = monthlyPremiumDisplayValue.split('.');
  return (
    <b className="text-quartary text-5xl md:text-6xl ">
      <span className="font-arial">{monthlyPremiumInteger}</span>
      <sup className="font-arial text-3xl md:text-4xl font-medium">{`.${monthlyPremiumDecimals}`}</sup>
      <p className="text-sm md:text-xl">Per Month</p>
      <p className="mt-2 text-primary text-xs md:text-sm font-medium">
        {monthInstall + ' monthly installments'}
      </p>
      <p className="text-primary text-xs md:text-sm font-extrabold">
        {'Due now: ' + formatCADCentsOptionalWithTwoDecimals.format(dueAmount)}
      </p>
    </b>
  );
};

const Premium: React.FC<PremiumInput> = (props) => {
  const { yearlyPremium, isEndorsement, paymentOptionData } = props;

  const maxObj =
    paymentOptionData && getAggregatedMonthlyPaymentOption(paymentOptionData);

  const [isMonth, setPeriod] = useState<boolean>(true);

  return (
    <div className="premium-container w-full m-auto p-auto">
      <div className="relative h-full">
        <div className="mt-2">
          {maxObj && !isEndorsement && (
            <>
              <b className={isMonth ? 'text-quartary' : 'text-faded'}>
                Monthly
              </b>
              <Boolean
                id="TogglePaymentPeriod"
                onChange={() => {
                  setPeriod(!isMonth);
                }}
                value={!isMonth}
              ></Boolean>
              <b className={!isMonth ? 'text-quartary' : 'text-faded'}>
                Yearly
              </b>
              <br />
            </>
          )}
          {maxObj && isMonth && !isEndorsement
            ? showMonthlyPremium(maxObj)
            : showYearlyPremium(yearlyPremium, isEndorsement)}
        </div>
      </div>
    </div>
  );
};

export default Premium;
